import { useEffect, useState } from 'react';
import { axiosInstance } from '../lib/api-client';
import PromotionalOrderModal from './PromotionalOrderModal';
import { toast } from 'sonner';
import { useAuthContext } from '../contexts/AuthContext';
import axios from 'axios';
import { BACKEND_CONFIG } from '../utils/productionConfig';
import { logger } from '../utils/logger';

declare global {
  interface Window {
    Razorpay: any;
  }
}

export function PromotionalOfferChecker() {
  const [hasActiveOffer, setHasActiveOffer] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [checking, setChecking] = useState(false);
  const { user, loadUser } = useAuthContext();
  const [gstSettings, setGstSettings] = useState<any>(null);

  useEffect(() => {
    if (user) {
      logger.log('🎁 [PromotionalOfferChecker] User detected, checking for promotional offers...');
      logger.log('   User:', user.firstName, user.lastName);
      logger.log('   User ID:', user._id);
      checkForActivePromotionalOrder();
      fetchGSTSettings();
    } else {
      logger.log('⚠️  [PromotionalOfferChecker] No user found, skipping offer check');
    }
  }, [user]);

  const fetchGSTSettings = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_CONFIG.apiUrl}/gst-settings`);
      setGstSettings(data.data);
    } catch (error) {
      // Silent fail
    }
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      // Check if already loaded
      if (window.Razorpay) {
        resolve(true);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const checkForActivePromotionalOrder = async () => {
    if (checking) return;
    
    try {
      setChecking(true);
      // Add timeout to prevent long waits in production
      const response = await axiosInstance.get('/promotional-orders/active', {
        timeout: 5000 // 5 second timeout
      });

      if (response.data.success && response.data.data) {
        setHasActiveOffer(true);
        setIsModalOpen(true);
      } else {
        setHasActiveOffer(false);
        setIsModalOpen(false);
      }
    } catch (error: any) {
      // Silently fail - don't show errors for this background check
      // This is expected if endpoint doesn't exist or times out
      setHasActiveOffer(false);
      setIsModalOpen(false);
    } finally {
      setChecking(false);
    }
  };

  const handlePayment = async (promotionalOrderId: string) => {
    try {
      // Load Razorpay script first
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error('Failed to load payment gateway. Please refresh the page.');
        return;
      }

      if (!window.Razorpay) {
        toast.error('Payment gateway not loaded. Please refresh the page.');
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please login to continue');
        return;
      }

      // Fetch the active promotional order (user's own)
      const { data: promoOrderData } = await axios.get(
        `${BACKEND_CONFIG.apiUrl}/promotional-orders/active`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (!promoOrderData.success || !promoOrderData.data) {
        toast.error('Promotional offer has expired');
        setIsModalOpen(false);
        return;
      }

      const promotionalOrder = promoOrderData.data;

      // Create payment order on backend with promotional order ID
      // Backend will calculate GST-inclusive amount
      const { data: orderData } = await axios.post(
        `${BACKEND_CONFIG.apiUrl}/payments/create-order`,
        {
          planId: promotionalOrder.planId._id,
          promotionalOrderId: promotionalOrderId, // Pass promotional order ID
          extraContacts: promotionalOrder.extraContacts,
          extraValidity: promotionalOrder.extraValidity
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const order = orderData.data;

      // Close the promotional modal
      setIsModalOpen(false);

      // Razorpay options
      const options = {
        key: order.razorpayKeyId,
        amount: order.razorpayOrder.amount, // Use razorpayOrder.amount (includes GST in paise)
        currency: 'INR',
        name: 'Atootbandan',
        description: `${promotionalOrder.planId.name} - Special Promotional Offer`,
        order_id: order.razorpayOrder.id, // Use razorpayOrder.id
        handler: async function (response: any) {
          try {
            // Verify payment on backend
            const { data: verifyData } = await axios.post(
              `${BACKEND_CONFIG.apiUrl}/payments/verify-payment`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                planId: promotionalOrder.planId._id,
                promotionalOrderId: promotionalOrderId // Pass promotional order ID for webhook
              },
              {
                headers: { Authorization: `Bearer ${token}` }
              }
            );

            if (verifyData.success) {
              toast.success('🎉 Payment successful! Your plan has been activated!');
              
              // Show bonus features if any
              const bonuses = [];
              if (promotionalOrder.extraContacts > 0) {
                bonuses.push(`+${promotionalOrder.extraContacts} extra contact views`);
              }
              if (promotionalOrder.extraValidity > 0) {
                bonuses.push(`+${promotionalOrder.extraValidity} days extra validity`);
              }
              
              if (bonuses.length > 0) {
                toast.success(`Bonus activated: ${bonuses.join(', ')}`);
              }

              // Reload user data
              await loadUser();
              
              // Check for new offers (in case there's another one)
              setTimeout(() => {
                checkForActivePromotionalOrder();
              }, 2000);
            }
          } catch (error: any) {
            toast.error('Payment verification failed. Please contact support.');
          }
        },
        prefill: {
          name: `${user?.firstName} ${user?.lastName}`,
          email: user?.email,
          contact: user?.phone
        },
        notes: {
          userId: user?._id,
          planId: promotionalOrder.planId._id,
          promotionalOrderId: promotionalOrderId
        },
        theme: {
          color: '#ec4899'
        },
        modal: {
          ondismiss: function() {
            toast.info('Payment cancelled');
            // Reopen the promotional modal
            setIsModalOpen(true);
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();

    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to initiate payment');
      setIsModalOpen(true); // Reopen modal on error
    }
  };

  const handleClose = () => {
    setIsModalOpen(false);
  };

  if (!hasActiveOffer) {
    return null;
  }

  return (
    <PromotionalOrderModal 
      isOpen={isModalOpen} 
      onClose={handleClose}
      onPayment={handlePayment}
    />
  );
}